import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import DepotsPage from './pages/DepotsPage';
import StockPage from './pages/StockPage';
import PreparationsPage from './pages/PreparationsPage';
import ReceptionsPage from './pages/ReceptionsPage';
import DocumentsPage from './pages/DocumentsPage';
import InventoriesPage from './pages/InventoriesPage';
import ScanPage from './pages/ScanPage';
import UsersPage from './pages/UsersPage';
import AssemblagesPage from './pages/AssemblagesPage';
import SettingsPage from './pages/SettingsPage';
import SecurityPage from './pages/SecurityPage';
import SnapshotsPage from './pages/SnapshotsPage';
import ClientsPage from './pages/ClientsPage';
import SuppliersPage from './pages/SuppliersPage';
import ImportPage from './pages/ImportPage';
import PyroDistrictPage from './pages/PyroDistrictPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Public Route (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Placeholder pages
const ComingSoonPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-[60vh] text-center">
    <div className="w-20 h-20 rounded-2xl bg-zinc-800 flex items-center justify-center mb-6">
      <span className="text-4xl">🚧</span>
    </div>
    <h1 className="text-2xl font-heading font-bold text-white mb-2">{title}</h1>
    <p className="text-zinc-400">Cette fonctionnalité sera bientôt disponible</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute>
          <ProductsPage />
        </ProtectedRoute>
      } />
      <Route path="/depots" element={
        <ProtectedRoute>
          <DepotsPage />
        </ProtectedRoute>
      } />
      <Route path="/stock" element={
        <ProtectedRoute>
          <StockPage />
        </ProtectedRoute>
      } />
      <Route path="/preparations" element={
        <ProtectedRoute>
          <PreparationsPage />
        </ProtectedRoute>
      } />
      <Route path="/assemblages" element={
        <ProtectedRoute>
          <AssemblagesPage />
        </ProtectedRoute>
      } />
      <Route path="/receptions" element={
        <ProtectedRoute>
          <ReceptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/inventories" element={
        <ProtectedRoute>
          <InventoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/scan" element={
        <ProtectedRoute>
          <ScanPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/suppliers" element={
        <ProtectedRoute>
          <SuppliersPage />
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute>
          <ImportPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/security" element={
        <ProtectedRoute>
          <SecurityPage />
        </ProtectedRoute>
      } />
      <Route path="/snapshots" element={
        <ProtectedRoute>
          <SnapshotsPage />
        </ProtectedRoute>
      } />
      <Route path="/pyrodistrict" element={
        <ProtectedRoute>
          <PyroDistrictPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#18181b',
              border: '1px solid #27272a',
              color: '#fff',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
